import { AppError } from '@app/errors/AppError'

import { createSelector } from '@app/utils/createSelector'
import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'

import type { V3TrainingPaymentContract } from '@app/packages/api/v3/types/lib'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callApi, callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

import { routingDateSelector } from './routing'
import { authIdentitySelector } from './session'

const contractSlice = createReduxSlice<SliceState<V3TrainingPaymentContract | null>>('training_payment_contract')

/**
 * The sitter's training debt, or `null` when there is none — they paid for training up front, or
 * they are not a sitter. Anyone who only wants to know whether to offer the page should ask
 * `trainingPaymentContractVisibleResultSelector` instead.
 */
export const trainingPaymentContractResultSelector = createSelector(
  [contractSlice.selector, routingDateSelector, authIdentitySelector],
  (sliceState, date, identity) =>
    createLoadable(c =>
      withCheckedSliceState(c, {
        sliceState,
        slice: contractSlice,
        // Per sitter, and it moves whenever an order is fulfilled or a bill is paid.
        hash: identity ?? 'anonymous',
        date: { date, expiresInMinutes: 5 },
        load: async dispatch => {
          const { getTrainingPaymentContract } = await import('@app/packages/api/v3/actions/training_payment_contract')
          try {
            return (await dispatch(callApi(getTrainingPaymentContract()))).data
          } catch (e) {
            // 404 is «no contract», including the empty-bodied one an undeployed endpoint answers
            // with — `AppError` rather than `ApiError` so both shapes are caught.
            if (e instanceof AppError && e.status === 404) return null
            throw e
          }
        },
      })
    )
)

/**
 * Whether the block is worth offering at all: there has to be a contract, and a settled one stops
 * being news a month after it was closed.
 */
export const trainingPaymentContractVisibleResultSelector = createSelector(
  [trainingPaymentContractResultSelector, routingDateSelector],
  (contractResult, date) => createLoadable((c): boolean => isWorthShowing(c.unwrap(contractResult), date))
)

const isWorthShowing = (contract: V3TrainingPaymentContract | null, now: Moment): boolean => {
  if (!contract) return false
  const { settled_on: settledOn } = contract.attributes
  if (!settledOn) return true
  return moment(settledOn).add(1, 'month').isSameOrAfter(now, 'day')
}

/**
 * «Оплатить». The payment page is minted per press rather than held: a link has a life of its own
 * server-side, and the one from a render five minutes ago is not the one to send anybody to.
 */
export const openTrainingPaymentBill = (billId: string) =>
  createThunk(async dispatch => {
    const { postBillsByIdPaymentLink } = await import('@app/packages/api/v2/actions/bills')
    const response = await dispatch(callV2Api(postBillsByIdPaymentLink(billId)))
    window.location.assign(response.data.attributes.payment_url)
  })

/**
 * «Закрыть сразу». The bill for the remainder is minted at press time and then paid like any
 * other. The server holds the contract's lock, so a second press gets the same bill and an offer
 * no longer on the table comes back `422`.
 */
export const settleTrainingPaymentContract = () =>
  createThunk(async dispatch => {
    const { postTrainingPaymentContractSettlement } = await import('@app/packages/api/v3/actions/training_payment_contract')
    const response = await dispatch(callApi(postTrainingPaymentContractSettlement()))
    // The bill exists from here on, whether or not the redirect below happens, so the card must
    // not go on offering «Закрыть сразу».
    dispatch(contractSlice.remove())
    await dispatch(openTrainingPaymentBill(response.data.id))
  })
